// ================================================================
// demo.ts — Demo 模式：開關管理 + 所有 Mock 資料
//
// 運作方式：
//   1. enableDemoMode() → 寫入 localStorage flag
//   2. AuthProvider 偵測到 flag → 使用 DEMO_SESSION（不呼叫 Workers）
//   3. api.ts 所有 fetch 函式 → 若 isDemoMode() 回傳 mock data
//
// Demo 情境：台灣創新集團（tigroup.com.tw），10 名使用者，14 天活動資料
// v2.1 新增：Gemini、Copilot Studio Agent 事件；擴充至 500 筆；新增 6 筆對話
// ================================================================

import type { EventRow, ConversationIndex, ConversationFull, TenantInfo, ApiKeyInfo } from './api';
import type { ApiKeySession } from './apikey-session';
import type { AgentInfo } from './agent-registry';

// ── Demo mode flag ────────────────────────────────────────────
const DEMO_FLAG = 'ai_platform_demo';

export function isDemoMode(): boolean {
  return localStorage.getItem(DEMO_FLAG) === '1';
}
export function enableDemoMode(): void {
  localStorage.setItem(DEMO_FLAG, '1');
}
export function disableDemoMode(): void {
  localStorage.removeItem(DEMO_FLAG);
}

// ── Demo session（作為 ApiKeySession 使用）────────────────────
export const DEMO_SESSION: ApiKeySession = {
  token:  'sess_demo',
  upn:    'admin@tigroup.com.tw',
  name:   'Demo 管理員',
  tid:    'demo-tenant-00000000-0000',
  roles:  ['TenantAdmin'],
  label:  'Demo 模式',
};

// ── Demo tenant ───────────────────────────────────────────────
export const DEMO_TENANT: TenantInfo = {
  tenant_id:     'demo-tenant-00000000-0000',
  name:          '台灣創新集團',
  domain:        'tigroup.com.tw',
  status:        'active',
  consent_state: 'granted',
};

// ── Demo API keys ─────────────────────────────────────────────
export const DEMO_API_KEYS: ApiKeyInfo[] = [
  {
    key_id: 'key-demo-001', key_prefix: 'aig_1a2b3c4d',
    label: '外部審計員', roles: '["Viewer"]',
    created_at: Math.floor(Date.now() / 1000) - 86400 * 5,
    last_used_at: Math.floor(Date.now() / 1000) - 3600,
    expires_at: Math.floor(Date.now() / 1000) + 86400 * 25,
  },
  {
    key_id: 'key-demo-002', key_prefix: 'aig_5e6f7g8h',
    label: 'SIEM 整合', roles: '["Viewer"]',
    created_at: Math.floor(Date.now() / 1000) - 86400 * 12,
    last_used_at: Math.floor(Date.now() / 1000) - 600,
    expires_at: null,
  },
];

// ── Helpers ───────────────────────────────────────────────────
// 確定性偽隨機（seed-based LCG）── 避免每次 reload 資料不同
function makePrng(seed: number) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 0xFFFFFFFF; };
}

function isoOffset(msAgo: number): string {
  return new Date(Date.now() - msAgo).toISOString();
}

// ── Static reference data ─────────────────────────────────────
const USERS: Array<{ upn: string; name: string; dept: string }> = [
  { upn: 'alice.chen@tigroup.com.tw',  name: '陳小華', dept: '業務部' },
  { upn: 'bob.wang@tigroup.com.tw',    name: '王大明', dept: '資訊部' },
  { upn: 'carol.lin@tigroup.com.tw',   name: '林美玲', dept: '行銷部' },
  { upn: 'david.wu@tigroup.com.tw',    name: '吳志偉', dept: '財務部' },
  { upn: 'emily.zhang@tigroup.com.tw', name: '張雅惠', dept: '人資部' },
  { upn: 'frank.liu@tigroup.com.tw',   name: '劉建宏', dept: '法務部' },
  { upn: 'grace.huang@tigroup.com.tw', name: '黃淑芬', dept: '研發部' },
  { upn: 'henry.chen@tigroup.com.tw',  name: '陳俊傑', dept: '業務部' },
  { upn: 'iris.lee@tigroup.com.tw',    name: '李怡君', dept: '行銷部' },
  { upn: 'james.ho@tigroup.com.tw',    name: '何建明', dept: '資訊部' },
];

const SERVICES = [
  { name: 'Microsoft Copilot', app: 'Microsoft 365', weight: 28 },
  { name: 'Copilot (Word)',    app: 'Word',          weight: 18 },
  { name: 'Copilot (Excel)',   app: 'Excel',         weight: 12 },
  { name: 'Copilot (Teams)',   app: 'Teams',         weight: 10 },
  { name: 'GitHub Copilot',   app: 'VS Code',        weight: 7  },
  { name: 'ChatGPT',          app: '瀏覽器',         weight: 8  },
  { name: 'Gemini',           app: '瀏覽器',         weight: 6  },
  { name: 'Perplexity.ai',    app: '瀏覽器',         weight: 4  },
  { name: 'Copilot Studio',   app: 'Power Platform', weight: 4  },
  { name: '🤖 HR 助理 Bot',   app: 'Teams',          weight: 2  },
  { name: '🤖 IT 服務台 Bot', app: 'Teams',          weight: 1  },
];

// 依 weight 展開成可直接 index 的 pool
const SERVICE_POOL: typeof SERVICES[0][] = SERVICES.flatMap(s =>
  Array.from({ length: s.weight }, () => s)
);

const EVENT_TYPES = ['interaction', 'interaction', 'interaction', 'visit', 'file_upload', 'export', 'dlp'];

// ── 產生 Demo Events（500 筆，14 天）────────────────────────────
export function buildDemoEvents(): EventRow[] {
  const rng = makePrng(0xDEADBEEF);
  const events: EventRow[] = [];
  const now = Date.now();

  for (let i = 0; i < 500; i++) {
    const daysAgo = rng() * 14;
    const msAgo   = daysAgo * 86400_000;
    const user    = USERS[Math.floor(rng() * USERS.length)];
    const svc     = SERVICE_POOL[Math.floor(rng() * SERVICE_POOL.length)];
    const evType  = EVENT_TYPES[Math.floor(rng() * EVENT_TYPES.length)];

    // DLP 觸發條件：外部服務（ChatGPT / Perplexity / Gemini）或隨機 5% 機率
    const isExternal = ['ChatGPT', 'Perplexity.ai', 'Gemini'].includes(svc.name);
    const dlp = evType === 'dlp' || (isExternal && rng() < 0.30) || rng() < 0.04;

    events.push({
      id:            `demo-evt-${i.toString().padStart(4, '0')}`,
      tenant_id:     DEMO_TENANT.tenant_id,
      event_time:    new Date(now - msAgo).toISOString(),
      user_upn:      user.upn,
      user_name:     user.name,
      department:    user.dept,
      ai_service:    svc.name,
      app_class:     svc.app,
      event_type:    dlp ? 'dlp' : evType,
      source_device: rng() < 0.7 ? 'Windows' : 'macOS',
      has_content:   evType === 'interaction',
      session_id:    `demo-session-${Math.floor(i / 5).toString().padStart(3, '0')}`,
      dlp_hit:       dlp,
    });
  }

  // 按時間由新到舊排序
  return events.sort((a, b) => b.event_time.localeCompare(a.event_time));
}

// Singleton（避免每次 call 都重算）
let _cachedEvents: EventRow[] | null = null;
export function getDemoEvents(): EventRow[] {
  if (!_cachedEvents) _cachedEvents = buildDemoEvents();
  return _cachedEvents;
}

// ── Demo Conversations（5 筆完整對話）─────────────────────────
export const DEMO_CONVERSATIONS: ConversationFull[] = [
  // ── 1. Copilot Word：合約摘要（正常）───────────────────────
  {
    session_id:  'demo-conv-001',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'frank.liu@tigroup.com.tw',
    user_name:   '劉建宏',
    department:  '法務部',
    ai_service:  'Copilot (Word)',
    start_time:  isoOffset(2 * 3600_000),
    dlp_hit:     false,
    msg_count:   4,
    messages: [
      {
        role: 'user', timestamp: isoOffset(2 * 3600_000),
        body: '請幫我摘要這份 NDA 合約的主要條款，特別是保密期限和違約罰則。',
        contexts: ['NDA_Contoso_2026.docx'],
      },
      {
        role: 'ai', timestamp: isoOffset(2 * 3600_000 - 8000),
        body: `根據您提供的 NDA 合約，主要條款摘要如下：\n\n**保密期限**\n合約簽署後 3 年內，雙方不得向第三方揭露任何機密資訊。合約終止後保密義務仍延續 2 年。\n\n**違約罰則**\n- 每次違約罰款新台幣 500 萬元\n- 因違約造成之實際損失，違約方需全額賠償\n- 情節重大者對方有權申請假處分\n\n**例外條款**\n以下情形不構成違約：(1) 資訊已屬公開領域；(2) 法院命令強制揭露。`,
      },
      {
        role: 'user', timestamp: isoOffset(2 * 3600_000 - 60000),
        body: '幫我把違約罰則改成條列式表格，並標注與業界標準的差異。',
      },
      {
        role: 'ai', timestamp: isoOffset(2 * 3600_000 - 68000),
        body: `已將違約罰則整理為比較表格：\n\n| 項目 | 本合約條款 | 業界常見標準 | 差異說明 |\n|------|-----------|-------------|----------|\n| 單次違約罰款 | NT$ 500 萬 | NT$ 100–300 萬 | 高於業界約 2–5 倍 |\n| 保密延續期限 | 終止後 2 年 | 終止後 1–3 年 | 符合業界範圍 |\n| 損害賠償上限 | 無上限 | 通常設有 3–5 倍上限 | 建議加入上限條款 |\n\n**建議：** 可考慮將單次罰款調整至 NT$ 200 萬，並加入損害賠償 3 倍上限條款，以降低合約談判阻力。`,
      },
    ],
  },

  // ── 2. ChatGPT：DLP 觸發（財務資料外洩）────────────────────
  {
    session_id:  'demo-conv-002',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'david.wu@tigroup.com.tw',
    user_name:   '吳志偉',
    department:  '財務部',
    ai_service:  'ChatGPT',
    start_time:  isoOffset(6 * 3600_000),
    dlp_hit:     true,
    msg_count:   3,
    messages: [
      {
        role: 'user', timestamp: isoOffset(6 * 3600_000),
        body: '幫我分析以下 Q2 財務數字並預測 Q3 趨勢：\n營收：NT$ 2,847,000,000\n毛利率：38.2%\n淨利：NT$ 312,000,000\nEBITDA：NT$ 498,000,000',
        dlp_alert: '偵測到機密財務資料：包含具體營收金額與利潤數字，透過未受管控的外部 AI 服務傳輸。政策 FIN-DLP-001 已觸發。',
      },
      {
        role: 'ai', timestamp: isoOffset(6 * 3600_000 - 5000),
        body: `根據您提供的 Q2 數據，分析如下：\n\n**現況評估**\n- 毛利率 38.2% 屬於科技製造業中上水平\n- EBITDA Margin 約 17.5%，現金流健康\n\n**Q3 趨勢預測**（保守估計）\n- 若維持相同毛利率，Q3 營收目標建議設定 NT$ 2.9–3.1B\n- 注意：Q3 通常因旺季效應有 5–8% 成長\n\n**風險提示**\n匯率波動（預估影響 ±1.2%）和原物料成本上漲需持續監控。`,
      },
      {
        role: 'user', timestamp: isoOffset(6 * 3600_000 - 120000),
        body: '再加上員工薪資總額 NT$ 892,000,000，幫我算出人力成本佔比。',
        dlp_alert: '再次偵測到機密財務資料：薪資總額數字。累計違規次數：2。',
      },
    ],
  },

  // ── 3. Copilot Teams：會議摘要（正常）──────────────────────
  {
    session_id:  'demo-conv-003',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'carol.lin@tigroup.com.tw',
    user_name:   '林美玲',
    department:  '行銷部',
    ai_service:  'Copilot (Teams)',
    start_time:  isoOffset(24 * 3600_000),
    dlp_hit:     false,
    msg_count:   3,
    messages: [
      {
        role: 'user', timestamp: isoOffset(24 * 3600_000),
        body: '幫我整理今天產品發布會議的重點和行動項目。',
        contexts: ['產品發布策略討論 - 2026-06-16 錄影'],
      },
      {
        role: 'ai', timestamp: isoOffset(24 * 3600_000 - 12000),
        body: `**會議摘要：產品發布策略討論**\n日期：2026-06-16 ｜ 參與者：7 人\n\n**重點決策**\n1. Q3 新品「AI-Copilot Pro」確認 8/15 發布日期\n2. 行銷預算調整至 NT$ 15M（較原計劃增加 20%）\n3. KOL 合作名單縮減至 5 人，提高單人預算\n\n**行動項目**\n| 負責人 | 任務 | 截止日 |\n|--------|------|-------|\n| 林美玲 | 完成上市素材套件 | 6/30 |\n| 李怡君 | KOL 合約洽談 | 7/5 |\n| 陳小華 | 通路通知信函 | 6/25 |\n\n**待確認事項**\n- 日本市場是否同步發布（待總部回覆）`,
      },
      {
        role: 'user', timestamp: isoOffset(24 * 3600_000 - 180000),
        body: '把行動項目轉成 Planner 卡片格式。',
      },
    ],
  },

  // ── 4. GitHub Copilot：程式碼協助（正常）───────────────────
  {
    session_id:  'demo-conv-004',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'grace.huang@tigroup.com.tw',
    user_name:   '黃淑芬',
    department:  '研發部',
    ai_service:  'GitHub Copilot',
    start_time:  isoOffset(48 * 3600_000),
    dlp_hit:     false,
    msg_count:   4,
    messages: [
      {
        role: 'user', timestamp: isoOffset(48 * 3600_000),
        body: '幫我寫一個 TypeScript function，從 Microsoft Graph API 取得過去 30 天的使用者登入日誌，並過濾出高風險登入。',
      },
      {
        role: 'ai', timestamp: isoOffset(48 * 3600_000 - 6000),
        body: "```typescript\nimport { Client } from '@microsoft/microsoft-graph-client';\n\ninterface SignInLog {\n  id: string;\n  userDisplayName: string;\n  userPrincipalName: string;\n  createdDateTime: string;\n  riskLevelDuringSignIn: string;\n  riskState: string;\n  location: { city: string; countryOrRegion: string };\n  ipAddress: string;\n}\n\nasync function getHighRiskSignIns(\n  client: Client,\n  daysBack = 30\n): Promise<SignInLog[]> {\n  const since = new Date();\n  since.setDate(since.getDate() - daysBack);\n  const filter = `createdDateTime ge ${since.toISOString()} and riskLevelDuringSignIn ne 'none'`;\n\n  const result = await client\n    .api('/auditLogs/signIns')\n    .filter(filter)\n    .select('id,userDisplayName,userPrincipalName,createdDateTime,riskLevelDuringSignIn,riskState,location,ipAddress')\n    .orderby('createdDateTime desc')\n    .top(200)\n    .get();\n\n  return result.value as SignInLog[];\n}\n```\n\n需要 `AuditLog.Read.All` 權限（Application 類型）。",
      },
      {
        role: 'user', timestamp: isoOffset(48 * 3600_000 - 120000),
        body: '加上 retry logic 和 throttling 處理。',
      },
      {
        role: 'ai', timestamp: isoOffset(48 * 3600_000 - 130000),
        body: "已加入 exponential backoff retry 和 429 throttling 處理：\n\n```typescript\nasync function withRetry<T>(\n  fn: () => Promise<T>,\n  maxRetries = 3\n): Promise<T> {\n  for (let attempt = 0; attempt < maxRetries; attempt++) {\n    try {\n      return await fn();\n    } catch (err: any) {\n      if (err.statusCode === 429) {\n        const retryAfter = parseInt(err.headers?.['retry-after'] ?? '5', 10);\n        await new Promise(r => setTimeout(r, retryAfter * 1000));\n      } else if (attempt === maxRetries - 1) {\n        throw err;\n      } else {\n        await new Promise(r => setTimeout(r, 2 ** attempt * 1000));\n      }\n    }\n  }\n  throw new Error('Max retries exceeded');\n}\n```",
      },
    ],
  },

  // ── 5. Copilot Excel：資料分析（正常）──────────────────────
  {
    session_id:  'demo-conv-005',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'alice.chen@tigroup.com.tw',
    user_name:   '陳小華',
    department:  '業務部',
    ai_service:  'Copilot (Excel)',
    start_time:  isoOffset(72 * 3600_000),
    dlp_hit:     false,
    msg_count:   3,
    messages: [
      {
        role: 'user', timestamp: isoOffset(72 * 3600_000),
        body: '這份業績報表有 500 筆訂單資料，幫我找出前 10 大客戶和他們的季度採購趨勢。',
        contexts: ['Q2_業績報表_2026.xlsx'],
      },
      {
        role: 'ai', timestamp: isoOffset(72 * 3600_000 - 10000),
        body: `根據您的業績報表，**前 10 大客戶**及季度趨勢如下：\n\n| 排名 | 客戶 | Q1 | Q2 | 成長率 |\n|------|------|----|----|--------|\n| 1 | 遠傳電信 | 4.2M | 5.8M | +38% ↑ |\n| 2 | 中華電信 | 3.9M | 4.1M | +5% → |\n| 3 | 台積電 | 3.5M | 4.8M | +37% ↑ |\n| 4 | 鴻海精密 | 2.8M | 2.6M | -7% ↓ |\n| 5 | 聯發科 | 2.5M | 3.2M | +28% ↑ |\n\n**洞察**\n- 電信客戶整體成長強勁（+20% avg）\n- 鴻海為唯一衰退帳戶，建議安排客戶拜訪\n- 台積電有成為第一大客戶的潛力`,
      },
      {
        role: 'user', timestamp: isoOffset(72 * 3600_000 - 300000),
        body: '幫我建立一個樞紐分析表，按業務員和產品類別交叉分析。',
      },
    ],
  },

  // ── 6. Gemini：DLP 觸發（個資外洩）────────────────────────────
  {
    session_id:  'demo-conv-006',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'emily.zhang@tigroup.com.tw',
    user_name:   '張雅惠',
    department:  '人資部',
    ai_service:  'Gemini',
    start_time:  isoOffset(10 * 3600_000),
    dlp_hit:     true,
    msg_count:   4,
    messages: [
      {
        role: 'user', timestamp: isoOffset(10 * 3600_000),
        body: '我需要草擬一封績效改善計劃（PIP）通知信，對象是員工 陳○○，工號 EMP2024-0317，目前薪資 NT$ 65,000，部門：研發部，主管：黃淑芬。',
        dlp_alert: '偵測到個人識別資訊（PII）：員工工號、薪資數字透過未受管控的外部 AI 服務（Gemini）傳輸。政策 HR-DLP-003 已觸發，HR 主管已收到通知。',
      },
      {
        role: 'ai', timestamp: isoOffset(10 * 3600_000 - 7000),
        body: `親愛的 陳○○ 您好，\n\n根據近期績效評估結果，您的工作表現在以下面向仍有改善空間：\n\n**一、改善目標（30 天）**\n1. 程式碼 Review 通過率提升至 90% 以上\n2. Sprint 任務準時完成率達 85%\n3. 每週主動與主管同步進度（書面記錄）\n\n**二、支援資源**\n- 公司提供一對一 Coaching（每週 1 小時）\n- 可申請線上技術培訓課程補助\n\n若在 30 天內達成上述目標，本計劃將自動終止。\n\n人力資源部`,
      },
      {
        role: 'user', timestamp: isoOffset(10 * 3600_000 - 180000),
        body: '再加上他去年的考核結果：「部分達成」（B-），並附上台北勞工局的申訴管道資訊。',
        dlp_alert: '偵測到人事考核資料（機密等級：限閱）外洩至外部 AI 服務。本次為第 2 次觸發，已升級為高嚴重度事件。',
      },
      {
        role: 'ai', timestamp: isoOffset(10 * 3600_000 - 190000),
        body: '已加入去年考核結果與申訴資訊。\n\n**申訴管道**\n台北市勞動局勞資爭議調解申請：\n- 電話：1999 轉 3\n- 線上申請：https://wlb.gov.taipei\n- 受理時間：週一至週五 09:00–17:00',
      },
    ],
  },

  // ── 7. HR 助理 Bot：請假申請（Copilot Studio Agent）────────────
  {
    session_id:  'demo-conv-007',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'henry.chen@tigroup.com.tw',
    user_name:   '陳俊傑',
    department:  '業務部',
    ai_service:  '🤖 HR 助理 Bot',
    start_time:  isoOffset(3 * 3600_000),
    dlp_hit:     false,
    msg_count:   5,
    messages: [
      {
        role: 'user', timestamp: isoOffset(3 * 3600_000),
        body: '我想申請 7/14 到 7/18 的特休假，共 5 天。',
      },
      {
        role: 'ai', timestamp: isoOffset(3 * 3600_000 - 3000),
        body: '您好，陳俊傑！\n\n我來幫您查詢特休假餘額……\n\n✅ **您目前的特休餘額：12 天**\n（本年度已使用：8 天 ｜ 總核配：20 天）\n\n申請 7/14–7/18（共 5 天）後，餘額將剩 **7 天**。\n\n請問要繼續送出申請嗎？',
      },
      {
        role: 'user', timestamp: isoOffset(3 * 3600_000 - 30000),
        body: '好，送出申請。代理人是業務部的 alice.chen@tigroup.com.tw。',
      },
      {
        role: 'ai', timestamp: isoOffset(3 * 3600_000 - 35000),
        body: '申請已送出！以下是申請摘要：\n\n| 項目 | 內容 |\n|------|------|\n| 假別 | 特休假 |\n| 起訖 | 2026/07/14 – 07/18 |\n| 天數 | 5 天 |\n| 代理人 | 陳小華（alice.chen@tigroup.com.tw） |\n| 狀態 | **待主管審核** |\n\n您的主管 王大明 將收到審核通知。通常 1–2 個工作天內完成審核。\n如有變更請在 24 小時內透過 HR Portal 撤回申請。',
      },
      {
        role: 'user', timestamp: isoOffset(3 * 3600_000 - 120000),
        body: '謝謝，另外幫我查一下今年還有幾天病假。',
      },
    ],
  },

  // ── 8. IT 服務台 Bot：密碼重設流程（Copilot Studio Agent）───────
  {
    session_id:  'demo-conv-008',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'iris.lee@tigroup.com.tw',
    user_name:   '李怡君',
    department:  '行銷部',
    ai_service:  '🤖 IT 服務台 Bot',
    start_time:  isoOffset(5 * 3600_000),
    dlp_hit:     false,
    msg_count:   6,
    messages: [
      {
        role: 'user', timestamp: isoOffset(5 * 3600_000),
        body: 'M365 帳號無法登入，顯示「密碼錯誤」，但我確定密碼是對的。',
      },
      {
        role: 'ai', timestamp: isoOffset(5 * 3600_000 - 4000),
        body: '您好，李怡君！讓我幫您診斷登入問題。\n\n我正在查詢您的帳號狀態……\n\n🔍 **診斷結果**\n- 帳號狀態：✅ 正常啟用\n- 登入失敗次數：**5 次**（過去 30 分鐘）\n- 帳號鎖定：⚠️ **已觸發安全鎖定（Smart Lockout）**\n\n**說明：** 連續輸入錯誤密碼後，Entra ID Smart Lockout 已暫時封鎖該帳號。這是保護機制，**並非駭客攻擊**。\n\n您要如何處理？\n(A) 等待 10 分鐘自動解鎖\n(B) 由我立即為您重設密碼（需驗證身份）',
      },
      {
        role: 'user', timestamp: isoOffset(5 * 3600_000 - 60000),
        body: '選 B，幫我重設密碼。',
      },
      {
        role: 'ai', timestamp: isoOffset(5 * 3600_000 - 65000),
        body: '好的，進行身份驗證。請提供以下其中一項：\n1. 員工證末 4 碼\n2. 您設定的安全問題答案\n3. 備用手機（目前設定的號碼末 3 碼顯示為 \*\*\*-\*\*5-12\*）',
      },
      {
        role: 'user', timestamp: isoOffset(5 * 3600_000 - 120000),
        body: '員工證末 4 碼：7823',
      },
      {
        role: 'ai', timestamp: isoOffset(5 * 3600_000 - 125000),
        body: '✅ 身份驗證通過！\n\n已向您的備用信箱 i\*\*\*@gmail.com 發送密碼重設連結。\n\n**注意事項：**\n- 連結 **30 分鐘**內有效\n- 新密碼需符合：12 碼以上、含大小寫英文、數字、特殊字元\n- 重設後建議在 MFA Authenticator App 更新\n\n如連結未收到，請回覆「重新發送」。此案件編號：INC-2026-06-17-4892',
      },
    ],
  },

  // ── 9. Perplexity.ai：競爭情報（DLP - 內部策略外洩）────────────
  {
    session_id:  'demo-conv-009',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'carol.lin@tigroup.com.tw',
    user_name:   '林美玲',
    department:  '行銷部',
    ai_service:  'Perplexity.ai',
    start_time:  isoOffset(30 * 3600_000),
    dlp_hit:     true,
    msg_count:   3,
    messages: [
      {
        role: 'user', timestamp: isoOffset(30 * 3600_000),
        body: '幫我搜尋競爭對手「瑞昱半導體」最近的產品發布資訊，我們公司正在規劃 Q4 的 AI-Copilot Pro 對抗策略，預算 NT$ 15M，主打企業端 B2B。',
        dlp_alert: '偵測到內部行銷策略資料外洩：產品代號（AI-Copilot Pro）、Q4 預算（NT$ 15M）、競爭定位資訊透過外部搜尋 AI 服務傳輸。政策 MKT-DLP-007 已觸發。',
      },
      {
        role: 'ai', timestamp: isoOffset(30 * 3600_000 - 8000),
        body: '根據最新公開資訊，瑞昱半導體近期動態：\n\n**產品面**\n- 2026 Q1 發布 RTL9210B NVMe controller，主打 PCIe 5.0\n- 正在布局 AI Edge 處理器，預計 2026 H2 量產\n\n**市場動態**\n- 與 Dell、HP 達成新的供應協議\n- 雲端服務業務年增 45%\n\n**弱點（B2B 角度）**\n- 企業服務支援體系較弱\n- 客製化彈性低於中小型競爭者',
      },
      {
        role: 'user', timestamp: isoOffset(30 * 3600_000 - 300000),
        body: '很好。我們 8/15 發布日前的 KOL 名單是：科技島讀、Mr. Market、陳寗，預算各 NT$ 1.5M，幫我想差異化 messaging。',
        dlp_alert: '偵測到機密行銷合作資訊：KOL 名單與個別預算透過外部 AI 服務傳輸。本次為第 2 次觸發，已記錄為重大違規事件。',
      },
    ],
  },

  // ── 10. Microsoft Copilot：人資政策問答（正常）──────────────────
  {
    session_id:  'demo-conv-010',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'bob.wang@tigroup.com.tw',
    user_name:   '王大明',
    department:  '資訊部',
    ai_service:  'Microsoft Copilot',
    start_time:  isoOffset(36 * 3600_000),
    dlp_hit:     false,
    msg_count:   4,
    messages: [
      {
        role: 'user', timestamp: isoOffset(36 * 3600_000),
        body: '公司的資訊安全政策對於使用外部 AI 工具有什麼規定？',
        contexts: ['台灣創新集團_資訊安全政策_v3.2.pdf'],
      },
      {
        role: 'ai', timestamp: isoOffset(36 * 3600_000 - 9000),
        body: `根據《台灣創新集團資訊安全政策 v3.2》（2026-01 更新），關於外部 AI 工具的規定摘要如下：\n\n**第 7.4 節：生成式 AI 使用規範**\n\n✅ **允許使用的情境**\n- 一般性知識查詢和學習\n- 非機密文件的文字潤飾\n- 公開資訊的彙整與摘要\n\n⛔ **禁止行為**\n- 將含有個資（姓名、工號、薪資）的資料輸入外部 AI\n- 上傳或貼上標記為「機密」或「限閱」的文件內容\n- 透過外部 AI 討論未公開的財務數字、產品計劃\n\n📋 **核准使用的外部 AI 服務**\n- Microsoft Copilot（透過 M365）— 企業合約保護\n- GitHub Copilot（需申請授權）\n- 其他服務需向資訊部申請評估`,
      },
      {
        role: 'user', timestamp: isoOffset(36 * 3600_000 - 120000),
        body: '我看到有同事在用 ChatGPT 和 Gemini 處理工作文件，這樣違規嗎？',
      },
      {
        role: 'ai', timestamp: isoOffset(36 * 3600_000 - 130000),
        body: '根據政策第 7.4 節，**使用 ChatGPT 和 Gemini 本身並非完全禁止**，但有條件限制：\n\n| 使用情境 | ChatGPT / Gemini | Microsoft Copilot |\n|---------|:---------------:|:-----------------:|\n| 一般知識問答 | ✅ 允許 | ✅ 允許 |\n| 機密文件摘要 | ⛔ 禁止 | ✅ 允許（受 M365 DLP 保護）|\n| 財務數字輸入 | ⛔ 禁止 | ⛔ 禁止 |\n| 個資處理 | ⛔ 禁止 | 需符合 PDPA 規定 |\n\n**建議：** 您可以向主管或資訊部舉報疑似違規行為，或透過公司的匿名通報管道（內網首頁 → 法遵通報）。',
      },
    ],
  },

  // ── 11. Gemini：程式碼輔助（正常）──────────────────────────────
  {
    session_id:  'demo-conv-011',
    tenant_id:   DEMO_TENANT.tenant_id,
    user_upn:    'james.ho@tigroup.com.tw',
    user_name:   '何建明',
    department:  '資訊部',
    ai_service:  'Gemini',
    start_time:  isoOffset(50 * 3600_000),
    dlp_hit:     false,
    msg_count:   4,
    messages: [
      {
        role: 'user', timestamp: isoOffset(50 * 3600_000),
        body: '幫我寫一個 Python script，自動從 Cloudflare D1 備份資料到 Azure Blob Storage，每天凌晨 2 點執行。',
      },
      {
        role: 'ai', timestamp: isoOffset(50 * 3600_000 - 7000),
        body: "```python\nimport os\nimport requests\nimport datetime\nfrom azure.storage.blob import BlobServiceClient\n\ndef backup_d1_to_blob():\n    # Cloudflare D1 export\n    cf_account = os.environ['CF_ACCOUNT_ID']\n    d1_db_id   = os.environ['CF_D1_DB_ID']\n    cf_token   = os.environ['CF_API_TOKEN']\n\n    export_url = f'https://api.cloudflare.com/client/v4/accounts/{cf_account}/d1/database/{d1_db_id}/export'\n    headers    = {'Authorization': f'Bearer {cf_token}'}\n    resp       = requests.post(export_url, headers=headers, json={'outputFormat': 'polling'})\n    resp.raise_for_status()\n\n    # Poll until ready\n    poll_url = resp.json()['result']['at_bookmark']\n    # ... polling logic omitted for brevity\n\n    # Upload to Azure Blob\n    date_str    = datetime.datetime.utcnow().strftime('%Y%m%d')\n    conn_str    = os.environ['AZURE_STORAGE_CONNECTION_STRING']\n    client      = BlobServiceClient.from_connection_string(conn_str)\n    container   = client.get_container_client('d1-backups')\n    blob_name   = f'backup_{date_str}.sql'\n\n    container.upload_blob(name=blob_name, data=sql_content, overwrite=True)\n    print(f'Backup complete: {blob_name}')\n\nif __name__ == '__main__':\n    backup_d1_to_blob()\n```\n\n排程用 cron 或 Azure Functions Timer Trigger 均可。建議將敏感 env vars 存入 Azure Key Vault。",
      },
      {
        role: 'user', timestamp: isoOffset(50 * 3600_000 - 180000),
        body: '加上 error handling 和 Slack 通知失敗警報。',
      },
      {
        role: 'ai', timestamp: isoOffset(50 * 3600_000 - 190000),
        body: "已加入 try/except 和 Slack Webhook 通知：\n\n```python\nimport traceback\n\ndef notify_slack(webhook_url: str, message: str, is_error: bool = False):\n    emoji = '🚨' if is_error else '✅'\n    payload = {'text': f'{emoji} D1 Backup: {message}'}\n    requests.post(webhook_url, json=payload, timeout=10)\n\ndef backup_d1_to_blob():\n    webhook = os.environ.get('SLACK_WEBHOOK_URL')\n    try:\n        # ... backup logic ...\n        if webhook:\n            notify_slack(webhook, f'成功備份至 Azure Blob: backup_{date_str}.sql')\n    except Exception as e:\n        err_msg = f'備份失敗: {str(e)}\\n```{traceback.format_exc()}```'\n        print(err_msg)\n        if webhook:\n            notify_slack(webhook, err_msg, is_error=True)\n        raise\n```",
      },
    ],
  },
];

// ConversationIndex view（列表頁用）
export const DEMO_CONVERSATION_LIST: ConversationIndex[] =
  DEMO_CONVERSATIONS.map(({ messages: _m, ...rest }) => rest);

// ── Demo Copilot Studio Agents（掃描結果 mock）────────────────
export const DEMO_AGENTS: AgentInfo[] = [
  {
    agentId:       'bot-001-hr-assistant',
    name:          'HR 助理 Bot',
    displayName:   'HR 助理 Bot',
    environment:   'Default (tigroup)',
    environmentId: 'env-00000001-default',
    publisher:     '張雅惠 (emily.zhang@tigroup.com.tw)',
    status:        'active',
    channels:      ['teams', 'webchat'],
    solutionName:  'HR_Solutions_v2',
    createdAt:     isoOffset(30 * 86400_000),
    discoveredAt:  isoOffset(2 * 3600_000),
    monitored:     true,
    tenantId:      'demo-tenant-00000000-0000',
  },
  {
    agentId:       'bot-002-it-helpdesk',
    name:          'IT 服務台 Bot',
    displayName:   'IT 服務台 Bot',
    environment:   'Default (tigroup)',
    environmentId: 'env-00000001-default',
    publisher:     '何建明 (james.ho@tigroup.com.tw)',
    status:        'active',
    channels:      ['teams', 'webchat', 'sharepoint'],
    solutionName:  'IT_HelpDesk_Suite',
    createdAt:     isoOffset(60 * 86400_000),
    discoveredAt:  isoOffset(2 * 3600_000),
    monitored:     true,
    tenantId:      'demo-tenant-00000000-0000',
  },
  {
    agentId:       'bot-003-sales-quote',
    name:          'SalesQuoteAgent',
    displayName:   '業務報價 Agent',
    environment:   'Sales Environment (tigroup)',
    environmentId: 'env-00000002-sales',
    publisher:     '陳小華 (alice.chen@tigroup.com.tw)',
    status:        'active',
    channels:      ['teams', 'directline'],
    solutionName:  'Sales_Automation',
    createdAt:     isoOffset(15 * 86400_000),
    discoveredAt:  isoOffset(2 * 3600_000),
    monitored:     false,
    tenantId:      'demo-tenant-00000000-0000',
  },
  {
    agentId:       'bot-004-finance-qa',
    name:          'FinanceQABot',
    displayName:   '財務問答 Bot',
    environment:   'Finance Environment (tigroup)',
    environmentId: 'env-00000003-finance',
    publisher:     '吳志偉 (david.wu@tigroup.com.tw)',
    status:        'inactive',
    channels:      ['webchat'],
    createdAt:     isoOffset(90 * 86400_000),
    discoveredAt:  isoOffset(2 * 3600_000),
    monitored:     false,
    tenantId:      'demo-tenant-00000000-0000',
  },
  {
    agentId:       'bot-005-legal-contract',
    name:          'ContractReviewAssistant',
    displayName:   '合約審查助理',
    environment:   'Default (tigroup)',
    environmentId: 'env-00000001-default',
    publisher:     '劉建宏 (frank.liu@tigroup.com.tw)',
    status:        'active',
    channels:      ['teams'],
    solutionName:  'Legal_Tools',
    createdAt:     isoOffset(7 * 86400_000),
    discoveredAt:  isoOffset(2 * 3600_000),
    monitored:     false,
    tenantId:      'demo-tenant-00000000-0000',
  },
  {
    agentId:       'bot-006-marketing-content',
    name:          'MarketingContentBot',
    displayName:   '行銷內容助手',
    environment:   'Marketing Environment (tigroup)',
    environmentId: 'env-00000004-marketing',
    publisher:     '林美玲 (carol.lin@tigroup.com.tw)',
    status:        'draft',
    channels:      ['webchat', 'directline'],
    createdAt:     isoOffset(3 * 86400_000),
    discoveredAt:  isoOffset(2 * 3600_000),
    monitored:     false,
    tenantId:      'demo-tenant-00000000-0000',
  },
];
